import { useEffect, useMemo, useState, useCallback } from "react";
import Layout, { PageHeader } from "../components/Layout";
import { api, money, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Plus, Trash, PencilSimple, MagnifyingGlass, Package, Wrench,
  Cube, ListBullets, Barcode, Gauge, Tag, ArrowUp, ArrowDown,
  ClockCounterClockwise, WarningCircle, ShieldWarning, CaretDown,
  ArrowsClockwise, Export,
} from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import RepairCatalogTab from "../components/RepairCatalogTab";
import PartsStockTab from "../components/PartsStockTab";

const EMPTY = { sku: "", name: "", category: "", kind: "accessory", price: 0, cost: 0, stock: 0, low_stock_threshold: 5, linked_manufacturer: "", linked_device_type: "", linked_model: "" };

const KIND_LABEL = { part: "Part", accessory: "Accessory", other: "Other" };

const TABS = [
  { id: "all", label: "All items", icon: ListBullets, kind: null, type: "product" },
  { id: "parts", label: "Parts", icon: Wrench, kind: "part", type: "parts_stock" },
  { id: "accessories", label: "Accessories", icon: Package, kind: "accessory", type: "product" },
  { id: "repair", label: "Repair", icon: Gauge, type: "repair" },
  { id: "shrinkage", label: "Shrinkage", icon: ShieldWarning, type: "shrinkage" },
];

const MOVEMENT_TYPES = [
  { id: "received", label: "Received", color: "text-emerald-600", bg: "bg-emerald-50", icon: ArrowDown },
  { id: "sale", label: "Sold", color: "text-blue-600", bg: "bg-blue-50", icon: Export },
  { id: "void_restock", label: "Void Restock", color: "text-amber-600", bg: "bg-amber-50", icon: ArrowsClockwise },
  { id: "adjustment", label: "Adjustment", color: "text-purple-600", bg: "bg-purple-50", icon: PencilSimple },
  { id: "damaged", label: "Damaged", color: "text-red-600", bg: "bg-red-50", icon: WarningCircle },
  { id: "returned", label: "Returned", color: "text-teal-600", bg: "bg-teal-50", icon: ArrowUp },
  { id: "transfer", label: "Transfer", color: "text-indigo-600", bg: "bg-indigo-50", icon: ArrowsClockwise },
  { id: "used_for_repair", label: "Used for Repair", color: "text-orange-600", bg: "bg-orange-50", icon: Wrench },
];

const ADJUST_TYPES = [
  { id: "received", label: "Received New Stock", desc: "Shipment arrived", sign: "+" },
  { id: "adjustment", label: "Stock Count Correction", desc: "Physical count differs", sign: "+/-" },
  { id: "damaged", label: "Damaged / Defective", desc: "Write off damaged unit", sign: "-" },
  { id: "returned", label: "Customer Return", desc: "Item returned to stock", sign: "+" },
];

export default function Inventory() {
  const [tab, setTab] = useState("all");
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState({ manufacturer: [], device_type: [], model: [], problem: [] });
  const [q, setQ] = useState("");
  const [stockFilter, setStockFilter] = useState("any");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const { user } = useAuth();

  // Stock adjustment modal
  const [adjustProduct, setAdjustProduct] = useState(null);
  const [adjustForm, setAdjustForm] = useState({ type: "received", quantity: "", reason: "" });
  const [adjusting, setAdjusting] = useState(false);

  // Stock history modal
  const [historyProduct, setHistoryProduct] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Catalog-tree filters
  const [mfrFilter, setMfrFilter] = useState("");
  const [deviceFilter, setDeviceFilter] = useState("");
  const [modelFilter, setModelFilter] = useState("");

  // Shrinkage
  const [shrinkageData, setShrinkageData] = useState(null);
  const [loadingShrinkage, setLoadingShrinkage] = useState(false);

  const activeTab = TABS.find((t) => t.id === tab) || TABS[0];

  const load = useCallback(() => api.get("/products", { params: { include_trade_ins: true } }).then((r) => setItems(r.data)), []);
  const loadCatalog = useCallback(() => api.get("/repair-catalog").then((r) => setCatalog(r.data || {})).catch(() => {}), []);
  useEffect(() => { load(); loadCatalog(); }, [load, loadCatalog]);

  // Load shrinkage when tab is active
  useEffect(() => {
    if (tab !== "shrinkage") return;
    setLoadingShrinkage(true);
    api.get("/stock-movements/shrinkage").then((r) => setShrinkageData(r.data)).catch(() => toast.error("Failed to load shrinkage report")).finally(() => setLoadingShrinkage(false));
  }, [tab]);

  // Distinct manufacturers, device types, models from products
  const catalogFilters = useMemo(() => {
    const mfrs = new Set(), devs = new Set(), models = new Set();
    items.forEach((p) => {
      if (p.linked_manufacturer) mfrs.add(p.linked_manufacturer);
      if (p.linked_device_type) devs.add(p.linked_device_type);
      if (p.linked_model) models.add(p.linked_model);
    });
    return {
      manufacturers: [...mfrs].sort(),
      deviceTypes: [...devs].sort(),
      models: [...models].sort(),
    };
  }, [items]);

  // Filtered device types and models based on selected manufacturer/device
  const filteredDeviceTypes = useMemo(() => {
    if (!mfrFilter) return catalogFilters.deviceTypes;
    const devs = new Set();
    items.forEach((p) => {
      if (p.linked_manufacturer === mfrFilter && p.linked_device_type) devs.add(p.linked_device_type);
    });
    return [...devs].sort();
  }, [items, mfrFilter, catalogFilters.deviceTypes]);

  const filteredModels = useMemo(() => {
    let base = items;
    if (mfrFilter) base = base.filter((p) => p.linked_manufacturer === mfrFilter);
    if (deviceFilter) base = base.filter((p) => p.linked_device_type === deviceFilter);
    const ms = new Set();
    base.forEach((p) => { if (p.linked_model) ms.add(p.linked_model); });
    return [...ms].sort();
  }, [items, mfrFilter, deviceFilter]);

  // Count badges per tab
  const counts = useMemo(() => {
    const byKind = (k) => items.filter((p) => (p.kind || "other") === k).length;
    const catalogTotal =
      (catalog.manufacturer || []).length +
      (catalog.device_type || []).length +
      (catalog.model || []).length +
      (catalog.problem || []).length;
    return {
      all: items.length,
      parts: byKind("part"),
      accessories: byKind("accessory"),
      repair: catalogTotal,
      shrinkage: "!",
    };
  }, [items, catalog]);

  const filteredProducts = useMemo(() => {
    const s = q.toLowerCase();
    let list = items;
    if (activeTab.kind) list = list.filter((p) => (p.kind || "other") === activeTab.kind);
    if (categoryFilter) list = list.filter((p) => (p.category || "").toLowerCase() === categoryFilter.toLowerCase());
    if (stockFilter === "in") list = list.filter((p) => p.stock > (p.low_stock_threshold || 0));
    else if (stockFilter === "low") list = list.filter((p) => p.stock > 0 && p.stock <= (p.low_stock_threshold || 0));
    else if (stockFilter === "out") list = list.filter((p) => p.stock <= 0);
    // Catalog-tree filters
    if (mfrFilter) list = list.filter((p) => p.linked_manufacturer === mfrFilter);
    if (deviceFilter) list = list.filter((p) => p.linked_device_type === deviceFilter);
    if (modelFilter) list = list.filter((p) => p.linked_model === modelFilter);
    if (s) {
      list = list.filter((p) =>
        p.name.toLowerCase().includes(s) ||
        p.sku.toLowerCase().includes(s) ||
        (p.category || "").toLowerCase().includes(s)
      );
    }
    const sorted = [...list];
    sorted.sort((a, b) => {
      if (sortBy === "price_asc") return (a.price || 0) - (b.price || 0);
      if (sortBy === "price_desc") return (b.price || 0) - (a.price || 0);
      if (sortBy === "stock_asc") return (a.stock || 0) - (b.stock || 0);
      if (sortBy === "stock_desc") return (b.stock || 0) - (a.stock || 0);
      return a.name.localeCompare(b.name);
    });
    return sorted;
  }, [items, q, activeTab, stockFilter, categoryFilter, sortBy, mfrFilter, deviceFilter, modelFilter]);

  // Distinct categories
  const categoryOptions = useMemo(() => {
    const base = activeTab.kind ? items.filter((p) => (p.kind || "other") === activeTab.kind) : items;
    const set = new Set();
    base.forEach((p) => { if (p.category) set.add(p.category); });
    return [...set].sort();
  }, [items, activeTab]);

  const open = (p) => {
    if (p) { setEditing(p); setForm({ ...EMPTY, ...p, kind: p.kind || "other" }); }
    else { setEditing(null); setForm({ ...EMPTY, kind: activeTab.kind || "accessory" }); }
    setShowModal(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price: Number(form.price || 0),
      cost: Number(form.cost || 0),
      stock: Number(form.stock || 0),
      low_stock_threshold: Number(form.low_stock_threshold || 0),
    };
    try {
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      setShowModal(false);
      await load();
      toast.success(editing ? "Product updated" : "Product added");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try { await api.delete(`/products/${p.id}`); await load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // Stock adjustment
  const openAdjust = (p) => {
    setAdjustProduct(p);
    setAdjustForm({ type: "received", quantity: "", reason: "" });
  };

  const submitAdjust = async () => {
    const qty = parseInt(adjustForm.quantity, 10);
    if (!qty || isNaN(qty)) { toast.error("Enter a valid quantity"); return; }
    // For "damaged" type, quantity should be negative (removing stock)
    const finalQty = adjustForm.type === "damaged" ? -Math.abs(qty) : qty;
    setAdjusting(true);
    try {
      const res = await api.post(`/products/${adjustProduct.id}/stock-adjust`, {
        type: adjustForm.type,
        quantity: finalQty,
        reason: adjustForm.reason,
      });
      toast.success(`Stock updated: ${res.data.stock_before} → ${res.data.stock_after}`);
      setAdjustProduct(null);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setAdjusting(false); }
  };

  // Stock history
  const openHistory = async (p) => {
    setHistoryProduct(p);
    setLoadingHistory(true);
    try {
      const res = await api.get(`/products/${p.id}/stock-history`);
      setHistory(res.data || []);
    } catch { toast.error("Failed to load history"); }
    finally { setLoadingHistory(false); }
  };

  const isProductTab = activeTab.type === "product";
  const isRepairTab = activeTab.type === "repair";
  const isShrinkageTab = activeTab.type === "shrinkage";
  const isPartsStockTab = activeTab.type === "parts_stock";
  const hasCatalogFilters = catalogFilters.manufacturers.length > 0;

  return (
    <Layout>
      <PageHeader
        overline="INVENTORY & CATALOG"
        title="Stock, parts & repair library"
        actions={
          <>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                data-testid="inventory-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={isRepairTab ? "Search brands, devices, models, issues…" : "Search inventory…"}
                className="w-64 h-11 pl-9 pr-3 border border-[#E5E7EB] bg-white focus:border-[#1E5FEB] focus:outline-none text-sm rounded-none"
              />
            </div>
            {isProductTab && (
              <button onClick={() => open(null)} data-testid="add-product-btn" className="h-11 px-4 bg-[#1E5FEB] text-white text-[13px] font-semibold flex items-center gap-2 hover:bg-[#1849C8]">
                <Plus size={14} weight="bold" /> ADD {activeTab.id === "parts" ? "PART" : activeTab.id === "accessories" ? "ACCESSORY" : "PRODUCT"}
              </button>
            )}
            <a
              href="/trade-ins"
              className="h-11 px-4 bg-white border border-[#E5E7EB] text-[#1E5FEB] text-[13px] font-semibold hover:bg-[#EFF6FF] hover:border-[#1E5FEB] flex items-center gap-2"
              data-testid="inventory-buy-device-btn"
            >
              <Plus size={14} weight="bold" /> BUY DEVICE
            </a>
          </>
        }
      />

      {/* Tabs */}
      <div className="px-6 pt-5 pb-1">
        <div className="inline-flex items-center gap-1 p-1 rounded-full bg-[#F3F4F6]" data-testid="inventory-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const n = counts[t.id];
            const active = t.id === tab;
            return (
              <button
                key={t.id}
                onClick={() => { setTab(t.id); setQ(""); setCategoryFilter(""); setStockFilter("any"); setMfrFilter(""); setDeviceFilter(""); setModelFilter(""); }}
                data-testid={`tab-${t.id}`}
                className={`h-10 px-4 rounded-full text-[12px] font-semibold inline-flex items-center gap-2 transition-all ${
                  active
                    ? "bg-white text-[#121216] shadow-sm"
                    : "text-[#4B5563] hover:text-[#121216]"
                }`}
              >
                <Icon size={14} weight="bold" />
                {t.label}
                {t.id !== "shrinkage" && (
                  <span className={`ml-0.5 text-[10px] font-mono px-1.5 py-0.5 rounded-full tabular-nums ${
                    active ? "bg-[#1E5FEB] text-white" : "bg-slate-200 text-slate-600"
                  }`}>{n}</span>
                )}
                {t.id === "shrinkage" && (
                  <span className={`ml-0.5 text-[10px] font-mono px-1.5 py-0.5 rounded-full ${
                    active ? "bg-red-500 text-white" : "bg-red-100 text-red-600"
                  }`}><ShieldWarning size={10} weight="bold" /></span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-6">
        {/* Filter toolbar */}
        {isProductTab && (
          <div className="mb-4 flex flex-col gap-3" data-testid="inventory-filter-bar">
            {/* Row 1: Stock status + sort */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="inline-flex items-center gap-1 p-0.5 rounded-full bg-[#F3F4F6]">
                {[
                  { id: "any", label: "All stock" },
                  { id: "in", label: "In stock" },
                  { id: "low", label: "Low" },
                  { id: "out", label: "Out" },
                ].map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setStockFilter(f.id)}
                    data-testid={`stock-${f.id}`}
                    className={`h-8 px-3 rounded-full text-[11px] font-semibold transition-all ${
                      stockFilter === f.id
                        ? "bg-white text-[#121216] shadow-sm"
                        : "text-[#4B5563] hover:text-[#121216]"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              {/* Category chips */}
              {categoryOptions.length > 0 && (
                <div className="inline-flex items-center gap-1 flex-wrap">
                  <button
                    onClick={() => setCategoryFilter("")}
                    className={`h-8 px-3 rounded-full text-[11px] font-semibold transition-all ${
                      !categoryFilter
                        ? "bg-[#121216] text-white"
                        : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"
                    }`}
                  >
                    All categories
                  </button>
                  {categoryOptions.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setCategoryFilter(cat === categoryFilter ? "" : cat)}
                      data-testid={`cat-${cat}`}
                      className={`h-8 px-3 rounded-full text-[11px] font-semibold transition-all ${
                        categoryFilter === cat
                          ? "bg-[#1E5FEB] text-white"
                          : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              )}

              <div className="ml-auto">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="h-8 pl-3 pr-8 rounded-full bg-white border border-slate-200 text-[11px] font-semibold text-slate-600 focus:border-[#1E5FEB] focus:outline-none"
                  data-testid="inventory-sort"
                >
                  <option value="name">Sort: Name A→Z</option>
                  <option value="price_asc">Sort: Price ↑</option>
                  <option value="price_desc">Sort: Price ↓</option>
                  <option value="stock_asc">Sort: Stock ↑</option>
                  <option value="stock_desc">Sort: Stock ↓</option>
                </select>
              </div>
            </div>

            {/* Row 2: Catalog tree filter (Manufacturer → Device → Model) */}
            {hasCatalogFilters && (
              <div className="flex items-center gap-2 flex-wrap" data-testid="catalog-tree-filter">
                <span className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold">Filter by device:</span>
                <select
                  value={mfrFilter}
                  onChange={(e) => { setMfrFilter(e.target.value); setDeviceFilter(""); setModelFilter(""); }}
                  data-testid="filter-manufacturer"
                  className="h-8 pl-3 pr-8 rounded-lg bg-white border border-slate-200 text-[12px] font-semibold text-slate-700 focus:border-[#1E5FEB] focus:outline-none"
                >
                  <option value="">All Brands</option>
                  {catalogFilters.manufacturers.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                <CaretDown size={10} className="text-zinc-400 -ml-1" />
                <select
                  value={deviceFilter}
                  onChange={(e) => { setDeviceFilter(e.target.value); setModelFilter(""); }}
                  data-testid="filter-device-type"
                  className="h-8 pl-3 pr-8 rounded-lg bg-white border border-slate-200 text-[12px] font-semibold text-slate-700 focus:border-[#1E5FEB] focus:outline-none"
                  disabled={!mfrFilter && filteredDeviceTypes.length === 0}
                >
                  <option value="">All Devices</option>
                  {filteredDeviceTypes.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                <CaretDown size={10} className="text-zinc-400 -ml-1" />
                <select
                  value={modelFilter}
                  onChange={(e) => setModelFilter(e.target.value)}
                  data-testid="filter-model"
                  className="h-8 pl-3 pr-8 rounded-lg bg-white border border-slate-200 text-[12px] font-semibold text-slate-700 focus:border-[#1E5FEB] focus:outline-none"
                  disabled={filteredModels.length === 0}
                >
                  <option value="">All Models</option>
                  {filteredModels.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                {(mfrFilter || deviceFilter || modelFilter) && (
                  <button
                    onClick={() => { setMfrFilter(""); setDeviceFilter(""); setModelFilter(""); }}
                    className="h-8 px-3 rounded-lg bg-red-50 text-red-600 text-[11px] font-semibold hover:bg-red-100"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Repair catalog */}
        {isRepairTab && (
          <RepairCatalogTab catalog={catalog} reloadCatalog={loadCatalog} q={q} />
        )}

        {/* Parts stock tree tab */}
        {isPartsStockTab && (
          <PartsStockTab catalog={catalog} reloadCatalog={loadCatalog} q={q} />
        )}

        {/* Shrinkage report tab */}
        {isShrinkageTab && (
          <ShrinkageReport data={shrinkageData} loading={loadingShrinkage} onRefresh={() => {
            setLoadingShrinkage(true);
            api.get("/stock-movements/shrinkage").then((r) => setShrinkageData(r.data)).finally(() => setLoadingShrinkage(false));
          }} />
        )}

        {/* Product table */}
        {isProductTab && (
          <div className="bg-white border border-[#E5E7EB] overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC] border-b border-[#E5E7EB]">
                <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-500">
                  <th className="p-3">SKU</th>
                  <th className="p-3">Name</th>
                  <th className="p-3">Kind</th>
                  <th className="p-3">Category</th>
                  <th className="p-3">Device</th>
                  <th className="p-3 text-right">Price</th>
                  <th className="p-3 text-right">Cost</th>
                  <th className="p-3 text-right">Stock</th>
                  <th className="p-3 w-36"></th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((p) => (
                  <tr key={p.id} className="border-b border-[#E5E7EB] hover:bg-[#F8FAFC]" data-testid={`product-row-${p.sku}`}>
                    <td className="p-3 font-mono text-xs">{p.sku}</td>
                    <td className="p-3 font-medium">{p.name}</td>
                    <td className="p-3"><KindPill kind={p.kind || "other"} /></td>
                    <td className="p-3 text-zinc-500">{p.category || "—"}</td>
                    <td className="p-3">
                      {p.linked_manufacturer ? (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-600">
                          {p.linked_manufacturer}{p.linked_device_type ? ` · ${p.linked_device_type}` : ""}{p.linked_model ? ` · ${p.linked_model}` : ""}
                        </span>
                      ) : <span className="text-zinc-300">—</span>}
                    </td>
                    <td className="p-3 text-right font-mono">${money(p.price)}</td>
                    <td className="p-3 text-right font-mono text-zinc-500">${money(p.cost)}</td>
                    <td className={`p-3 text-right font-mono font-bold ${p.stock <= 0 ? "text-red-600" : p.stock <= (p.low_stock_threshold || 0) ? "text-amber-600" : ""}`}>
                      {p.stock}
                      {p.stock <= 0 && <span className="ml-1 text-[9px] font-bold text-red-500 bg-red-50 px-1 py-0.5 rounded">OUT</span>}
                      {p.stock > 0 && p.stock <= (p.low_stock_threshold || 0) && <span className="ml-1 text-[9px] font-bold text-amber-500 bg-amber-50 px-1 py-0.5 rounded">LOW</span>}
                    </td>
                    <td className="p-3">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => openAdjust(p)}
                          data-testid={`adjust-${p.sku}`}
                          title="Adjust stock"
                          className="p-2 border border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-500 hover:bg-emerald-100 rounded"
                        >
                          <ArrowUp size={14} />
                        </button>
                        <button
                          onClick={() => openHistory(p)}
                          data-testid={`history-${p.sku}`}
                          title="Stock history"
                          className="p-2 border border-blue-200 bg-blue-50 text-blue-700 hover:border-blue-500 hover:bg-blue-100 rounded"
                        >
                          <ClockCounterClockwise size={14} />
                        </button>
                        <button onClick={() => open(p)} data-testid={`edit-${p.sku}`} className="p-2 border border-[#E5E7EB] hover:border-[#121216] rounded"><PencilSimple size={14} /></button>
                        {user?.role === "admin" && (
                          <button onClick={() => del(p)} className="p-2 border border-[#E5E7EB] hover:border-[#DC2626] hover:text-[#DC2626] rounded"><Trash size={14} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredProducts.length === 0 && (
                  <tr><td colSpan="9" className="p-12 text-center">
                    <Package size={36} weight="duotone" className="mx-auto text-zinc-300 mb-2" />
                    <div className="text-sm text-zinc-500">No {activeTab.label.toLowerCase()} yet.</div>
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Product edit modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white w-full max-w-xl rounded-[20px] overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto" data-testid="product-modal">
            <div className="border-b border-[#E5E7EB] p-5">
              <div className="overline mb-1">{editing ? "EDIT" : "ADD"} PRODUCT</div>
              <h2 className="font-heading font-bold text-2xl tracking-tight">{editing ? form.name : `New ${KIND_LABEL[form.kind]?.toLowerCase() || "product"}`}</h2>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Field label="Type">
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { id: "part", label: "Part", icon: Wrench },
                      { id: "accessory", label: "Accessory", icon: Package },
                      { id: "other", label: "Other", icon: Tag },
                    ].map((k) => (
                      <button
                        key={k.id}
                        type="button"
                        onClick={() => setForm({ ...form, kind: k.id })}
                        data-testid={`kind-${k.id}`}
                        className={`h-11 rounded-xl border text-[12px] font-semibold flex items-center justify-center gap-1.5 transition-all ${
                          form.kind === k.id
                            ? "border-[#1E5FEB] bg-blue-50 text-[#1E5FEB]"
                            : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"
                        }`}
                      >
                        <k.icon size={13} weight="bold" /> {k.label.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </Field>
              </div>
              <Field label="SKU"><input required value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className={fld + " font-mono"} data-testid="prod-sku" /></Field>
              <Field label="Category"><input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. Screen, Battery" className={fld} /></Field>
              <div className="col-span-2"><Field label="Name"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={fld} data-testid="prod-name" /></Field></div>
              <Field label="Price ($)"><input required type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={fld + " font-mono"} data-testid="prod-price" /></Field>
              <Field label="Cost ($)"><input type="number" min="0" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className={fld + " font-mono"} /></Field>
              <Field label="Stock"><input required type="number" min="0" step="1" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className={fld + " font-mono"} data-testid="prod-stock" /></Field>
              <Field label="Low stock alert"><input type="number" min="0" step="1" value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })} className={fld + " font-mono"} /></Field>

              {/* Device linking */}
              <div className="col-span-2 border-t border-dashed border-slate-200 pt-3 mt-1">
                <div className="text-[11px] uppercase tracking-[0.15em] text-zinc-400 font-semibold mb-2">Link to device (for filtering & shrinkage tracking)</div>
                <div className="grid grid-cols-3 gap-2">
                  <Field label="Brand"><input value={form.linked_manufacturer || ""} onChange={(e) => setForm({ ...form, linked_manufacturer: e.target.value })} placeholder="e.g. Apple" className={fld} data-testid="prod-mfr" /></Field>
                  <Field label="Device"><input value={form.linked_device_type || ""} onChange={(e) => setForm({ ...form, linked_device_type: e.target.value })} placeholder="e.g. iPhone" className={fld} data-testid="prod-device" /></Field>
                  <Field label="Model"><input value={form.linked_model || ""} onChange={(e) => setForm({ ...form, linked_model: e.target.value })} placeholder="e.g. iPhone 15 Pro" className={fld} data-testid="prod-model" /></Field>
                </div>
              </div>
            </div>
            <div className="flex border-t border-[#E5E7EB]">
              <button type="button" onClick={() => setShowModal(false)} className="flex-1 h-12 text-sm font-semibold">CANCEL</button>
              <button type="submit" data-testid="prod-save" className="flex-1 h-12 bg-[#121216] hover:bg-[#1E5FEB] text-white text-sm font-semibold">SAVE</button>
            </div>
          </form>
        </div>
      )}

      {/* Stock adjustment modal */}
      {adjustProduct && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setAdjustProduct(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md rounded-[20px] overflow-hidden shadow-2xl" data-testid="stock-adjust-modal">
            <div className="border-b border-[#E5E7EB] p-5">
              <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold mb-1">STOCK ADJUSTMENT</div>
              <h2 className="font-heading font-bold text-xl tracking-tight">{adjustProduct.name}</h2>
              <div className="flex items-center gap-3 mt-2">
                <span className="text-sm text-zinc-500">Current stock:</span>
                <span className="font-mono font-bold text-lg">{adjustProduct.stock}</span>
                <span className="text-xs text-zinc-400 font-mono">SKU: {adjustProduct.sku}</span>
              </div>
            </div>
            <div className="p-5 space-y-4">
              {/* Type selector */}
              <div className="space-y-2">
                {ADJUST_TYPES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setAdjustForm({ ...adjustForm, type: t.id })}
                    data-testid={`adjust-type-${t.id}`}
                    className={`w-full text-left p-3 rounded-xl border transition-all ${
                      adjustForm.type === t.id
                        ? "border-[#1E5FEB] bg-blue-50"
                        : "border-slate-200 hover:border-slate-400"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${adjustForm.type === t.id ? "bg-[#1E5FEB] text-white" : "bg-slate-100 text-slate-500"}`}>{t.sign}</span>
                      <span className="text-sm font-semibold">{t.label}</span>
                    </div>
                    <div className="text-[11px] text-zinc-400 mt-0.5 ml-10">{t.desc}</div>
                  </button>
                ))}
              </div>

              {/* Quantity */}
              <Field label="Quantity">
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={adjustForm.quantity}
                  onChange={(e) => setAdjustForm({ ...adjustForm, quantity: e.target.value })}
                  placeholder="How many units?"
                  className={fld + " font-mono text-lg"}
                  data-testid="adjust-quantity"
                  autoFocus
                />
              </Field>

              {/* Reason */}
              <Field label={adjustForm.type === "adjustment" || adjustForm.type === "damaged" ? "Reason (required)" : "Reason (optional)"}>
                <textarea
                  value={adjustForm.reason}
                  onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })}
                  placeholder="e.g. New shipment from supplier, counted 3 extra on shelf..."
                  className="w-full h-20 px-3 py-2 border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none text-sm rounded-none bg-white resize-none"
                  data-testid="adjust-reason"
                />
              </Field>
            </div>
            <div className="flex border-t border-[#E5E7EB]">
              <button type="button" onClick={() => setAdjustProduct(null)} className="flex-1 h-12 text-sm font-semibold" disabled={adjusting}>CANCEL</button>
              <button
                type="button"
                onClick={submitAdjust}
                disabled={adjusting || !adjustForm.quantity}
                data-testid="adjust-submit"
                className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50"
              >
                {adjusting ? "SAVING..." : "CONFIRM ADJUSTMENT"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stock history modal */}
      {historyProduct && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setHistoryProduct(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-2xl rounded-[20px] overflow-hidden shadow-2xl max-h-[85vh] flex flex-col" data-testid="stock-history-modal">
            <div className="border-b border-[#E5E7EB] p-5">
              <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold mb-1">STOCK MOVEMENT HISTORY</div>
              <h2 className="font-heading font-bold text-xl tracking-tight">{historyProduct.name}</h2>
              <div className="text-xs text-zinc-400 font-mono mt-1">SKU: {historyProduct.sku} · Current stock: {historyProduct.stock}</div>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {loadingHistory ? (
                <div className="text-center py-8 text-zinc-400">Loading...</div>
              ) : history.length === 0 ? (
                <div className="text-center py-8">
                  <ClockCounterClockwise size={32} className="mx-auto text-zinc-300 mb-2" />
                  <div className="text-sm text-zinc-500">No stock movements recorded yet.</div>
                  <div className="text-xs text-zinc-400 mt-1">Movements will appear here as stock changes are logged.</div>
                </div>
              ) : (
                <div className="space-y-2">
                  {history.map((m) => {
                    const mt = MOVEMENT_TYPES.find((t) => t.id === m.type) || MOVEMENT_TYPES[0];
                    const Icon = mt.icon;
                    return (
                      <div key={m.id} className={`flex items-start gap-3 p-3 rounded-xl border border-slate-100 ${mt.bg}`} data-testid={`movement-${m.id}`}>
                        <div className={`p-2 rounded-lg bg-white ${mt.color}`}><Icon size={16} weight="bold" /></div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-semibold ${mt.color}`}>{mt.label}</span>
                            <span className={`text-xs font-mono font-bold ${m.quantity_change > 0 ? "text-emerald-600" : "text-red-600"}`}>
                              {m.quantity_change > 0 ? "+" : ""}{m.quantity_change}
                            </span>
                            <span className="text-xs text-zinc-400 font-mono">{m.quantity_before} → {m.quantity_after}</span>
                          </div>
                          {m.reason && <div className="text-xs text-zinc-600 mt-0.5">{m.reason}</div>}
                          <div className="flex items-center gap-2 mt-1 text-[10px] text-zinc-400">
                            <span>by {m.performed_by_name || "System"}</span>
                            <span>·</span>
                            <span>{new Date(m.created_at).toLocaleString()}</span>
                            {m.sale_number && <><span>·</span><span>Sale #{m.sale_number}</span></>}
                            {m.ticket_number && <><span>·</span><span>Ticket #{m.ticket_number}</span></>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="border-t border-[#E5E7EB] p-3 flex justify-end">
              <button onClick={() => setHistoryProduct(null)} className="h-10 px-6 bg-[#121216] text-white text-sm font-semibold rounded-lg" data-testid="history-close">CLOSE</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}


/* ────── Shrinkage Report Component ────── */

function ShrinkageReport({ data, loading, onRefresh }) {
  if (loading) return <div className="text-center py-16 text-zinc-400">Loading shrinkage report...</div>;
  if (!data) return <div className="text-center py-16 text-zinc-400">No data available.</div>;

  const flagged = data.items.filter((r) => r.shrinkage > 0);

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-4 gap-4">
        <KpiCard label="Total Products" value={data.total_products} sub="tracked" />
        <KpiCard label="With Shrinkage" value={data.products_with_shrinkage} sub="items missing" color={data.products_with_shrinkage > 0 ? "red" : "green"} />
        <KpiCard label="Shrinkage Value" value={`$${Math.abs(data.total_shrinkage_value).toFixed(2)}`} sub="at cost" color={data.total_shrinkage_value > 0 ? "red" : "green"} />
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 flex items-center justify-center">
          <button onClick={onRefresh} className="h-10 px-6 bg-[#1E5FEB] text-white text-[12px] font-semibold rounded-lg flex items-center gap-2 hover:bg-[#1849C8]" data-testid="refresh-shrinkage">
            <ArrowsClockwise size={14} weight="bold" /> Refresh Report
          </button>
        </div>
      </div>

      {flagged.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <ShieldWarning size={18} weight="bold" className="text-red-600" />
            <span className="text-sm font-bold text-red-800">Items with unexplained stock loss</span>
          </div>
          <p className="text-xs text-red-600 mb-3">
            These items have fewer units in stock than expected based on received shipments minus sales. Investigate possible theft, damage, or data entry errors.
          </p>
          <div className="bg-white border border-red-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-red-50">
                <tr className="text-left text-[10px] uppercase tracking-wider text-red-500">
                  <th className="p-2">SKU</th>
                  <th className="p-2">Product</th>
                  <th className="p-2 text-right">Received</th>
                  <th className="p-2 text-right">Sold</th>
                  <th className="p-2 text-right">Expected</th>
                  <th className="p-2 text-right">Actual</th>
                  <th className="p-2 text-right">Missing</th>
                  <th className="p-2 text-right">$ Lost</th>
                </tr>
              </thead>
              <tbody>
                {flagged.map((r) => (
                  <tr key={r.product_id} className="border-t border-red-100" data-testid={`shrinkage-row-${r.sku}`}>
                    <td className="p-2 font-mono text-xs">{r.sku}</td>
                    <td className="p-2 font-medium">{r.name}</td>
                    <td className="p-2 text-right font-mono">{r.received}</td>
                    <td className="p-2 text-right font-mono">{r.sold}</td>
                    <td className="p-2 text-right font-mono">{r.expected_stock}</td>
                    <td className="p-2 text-right font-mono">{r.current_stock}</td>
                    <td className="p-2 text-right font-mono font-bold text-red-600">{r.shrinkage}</td>
                    <td className="p-2 text-right font-mono font-bold text-red-600">${r.shrinkage_value.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Full table */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
        <div className="p-4 border-b border-[#E5E7EB]">
          <h3 className="text-sm font-bold">All Products — Stock vs Expected</h3>
          <p className="text-xs text-zinc-400 mt-0.5">Compare expected stock (based on logged movements) against actual stock counts</p>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[#F8FAFC]">
            <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500">
              <th className="p-3">SKU</th>
              <th className="p-3">Product</th>
              <th className="p-3">Kind</th>
              <th className="p-3">Device</th>
              <th className="p-3 text-right">Received</th>
              <th className="p-3 text-right">Sold</th>
              <th className="p-3 text-right">Damaged</th>
              <th className="p-3 text-right">Expected</th>
              <th className="p-3 text-right">Actual</th>
              <th className="p-3 text-right">Diff</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((r) => (
              <tr key={r.product_id} className="border-t border-[#E5E7EB] hover:bg-[#F8FAFC]">
                <td className="p-3 font-mono text-xs">{r.sku}</td>
                <td className="p-3 font-medium">{r.name}</td>
                <td className="p-3"><KindPill kind={r.kind} /></td>
                <td className="p-3 text-xs text-zinc-400">
                  {r.linked_manufacturer || "—"}
                  {r.linked_device_type ? ` · ${r.linked_device_type}` : ""}
                </td>
                <td className="p-3 text-right font-mono text-emerald-600">{r.received || "—"}</td>
                <td className="p-3 text-right font-mono text-blue-600">{r.sold || "—"}</td>
                <td className="p-3 text-right font-mono text-red-600">{r.damaged || "—"}</td>
                <td className="p-3 text-right font-mono">{r.expected_stock}</td>
                <td className="p-3 text-right font-mono font-bold">{r.current_stock}</td>
                <td className={`p-3 text-right font-mono font-bold ${
                  r.shrinkage > 0 ? "text-red-600" : r.shrinkage < 0 ? "text-emerald-600" : "text-zinc-400"
                }`}>
                  {r.shrinkage > 0 ? `-${r.shrinkage}` : r.shrinkage < 0 ? `+${Math.abs(r.shrinkage)}` : "0"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/* ────── Shared UI Components ────── */

function KpiCard({ label, value, sub, color = "default" }) {
  const colorMap = {
    default: "text-[#121216]",
    red: "text-red-600",
    green: "text-emerald-600",
  };
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
      <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${colorMap[color]}`}>{value}</div>
      {sub && <div className="text-[11px] text-zinc-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function KindPill({ kind }) {
  const map = {
    part: { bg: "bg-blue-50", text: "text-[#1E5FEB]", label: "PART" },
    accessory: { bg: "bg-emerald-50", text: "text-emerald-700", label: "ACCESSORY" },
    other: { bg: "bg-slate-100", text: "text-slate-600", label: "OTHER" },
  };
  const s = map[kind] || map.other;
  return (
    <span className={`text-[10px] font-bold tracking-wider px-2 py-1 rounded-full ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
}

const fld = "w-full h-10 px-3 border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none text-sm rounded-none bg-white";
function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-semibold mb-1">{label}</div>
      {children}
    </label>
  );
}
